import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { FullSpinner } from "@/components/States";
import { toast } from "sonner";
import { Search } from "lucide-react";

export default function AdminFeatureFlags() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [confirm, setConfirm] = useState(null);

  const load = () => api.get("/admin/feature-flags").then(({ data }) => setData(data));
  useEffect(() => { load(); }, []);

  const doToggle = async (flag, enabled, reason) => {
    try {
      await api.patch(`/admin/feature-flags/${flag.key}`, { enabled, reason });
      toast.success(`${flag.name} ${enabled ? "enabled" : "disabled"}`);
      setConfirm(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const onToggle = (flag) => {
    const next = !flag.enabled;
    if (!next) { setConfirm({ flag, reason: "" }); return; }
    doToggle(flag, true, "");
  };

  if (!data) return <FullSpinner label="Loading feature flags…" />;
  const cats = [...new Set(data.flags.map((f) => f.category))];
  const visible = data.flags.filter((f) => {
    if (filter === "enabled" && !f.enabled) return false;
    if (filter === "disabled" && f.enabled) return false;
    if (q && !(`${f.name} ${f.key} ${f.category}`.toLowerCase().includes(q.toLowerCase()))) return false;
    return true;
  });

  return (
    <div className="space-y-5" data-testid="admin-feature-flags">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-serif text-3xl font-bold">Feature Flags</h1>
        <div className="flex gap-3 text-sm">
          <span className="text-emerald-300">🟢 {data.summary.enabled} on</span>
          <span className="text-[#8A6D71]">⚪ {data.summary.disabled} off</span>
          <span className="text-[#D8B4B9]">{data.summary.total} total</span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 bg-[#2A0F14] border border-[#781826]/40 rounded-xl px-3 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-[#C59B27]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search features" data-testid="flag-search"
            className="flex-1 bg-transparent py-2.5 text-sm outline-none text-[#FAF5F0]" />
        </div>
        {["all", "enabled", "disabled"].map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`flag-filter-${f}`}
            className={`text-sm px-4 py-2 rounded-full capitalize ${filter === f ? "bg-[#C59B27] text-[#3B0A12]" : "bg-[#2A0F14] text-[#D8B4B9]"}`}>{f}</button>
        ))}
      </div>

      {cats.map((cat) => {
        const items = visible.filter((f) => f.category === cat);
        if (!items.length) return null;
        return (
          <div key={cat} className="space-y-2">
            <h2 className="text-xs uppercase tracking-wide text-[#C59B27] font-semibold">{cat}</h2>
            {items.map((f) => (
              <div key={f.key} className="bg-[#2A0F14] border border-[#781826]/40 rounded-2xl p-4 flex items-center justify-between gap-3" data-testid={`flag-row-${f.key}`}>
                <div className="min-w-0">
                  <div className="font-medium flex items-center gap-2">
                    {f.name}
                    {f.critical && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-900/40 text-amber-300">CRITICAL</span>}
                  </div>
                  <div className="text-xs text-[#D8B4B9]">{f.description}</div>
                  <div className="text-[11px] text-[#8A6D71] font-mono mt-0.5">{f.key}{f.updated_by ? ` · by ${f.updated_by}` : ""}</div>
                </div>
                <button onClick={() => onToggle(f)} data-testid={`flag-toggle-${f.key}`}
                  className={`relative w-12 h-6 rounded-full shrink-0 transition ${f.enabled ? "bg-emerald-500" : "bg-[#4a2830]"}`}>
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full bg-white transition ${f.enabled ? "left-6" : "left-0.5"}`} />
                </button>
              </div>
            ))}
          </div>
        );
      })}

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="flag-confirm-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setConfirm(null)} />
          <div className="relative bg-[#1A090D] border border-[#781826] rounded-2xl p-5 w-full max-w-md space-y-3">
            <h3 className="font-serif text-xl font-bold">Disable {confirm.flag.name}?</h3>
            <p className="text-sm text-[#D8B4B9]">This hides the feature from users, blocks its APIs and stops related activity. Existing data is preserved.</p>
            <textarea value={confirm.reason} onChange={(e) => setConfirm({ ...confirm, reason: e.target.value })}
              placeholder="Reason (recorded in audit log)" data-testid="flag-disable-reason"
              className="w-full bg-[#2A0F14] border border-[#781826]/40 rounded-lg px-3 py-2 text-sm text-white" />
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirm(null)} className="text-sm px-4 py-2 rounded-lg bg-[#2A0F14] text-[#D8B4B9]">Cancel</button>
              <button onClick={() => doToggle(confirm.flag, false, confirm.reason)} data-testid="flag-disable-confirm"
                className="text-sm px-4 py-2 rounded-lg bg-rose-700 text-white">Disable Feature</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
